import React, { useState, useCallback } from 'react';
import { Dropdown, Modal, Radio, Space, Card } from 'antd';
import { User, LogOut, Palette } from 'lucide-react';
import { Paintbrush } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../stores/auth-store';
import { useTheme } from '../../contexts/ThemeContext';

export default function ProfileChip() {
  const { logout, userEmail, userProfile, userRole } = useAuth();
  const { themes, currentTheme, changeTheme } = useTheme();
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    logout();
    navigate('/my-dashboard');
  }, [logout, navigate]);

  const displayName = userProfile?.display_name || (userEmail ? userEmail.split('@')[0] : 'User');
  const lightThemes = Object.entries(themes).filter(([, t]) => t.mode === 'light');
  const darkThemes = Object.entries(themes).filter(([, t]) => t.mode === 'dark');
  const currentMode = themes[currentTheme].mode;

  const menuItems = [
    {
      key: 'profile',
      icon: <User size={14} />,
      label: (
        <span>
          {displayName}
          <span style={{ marginLeft: 6, opacity: 0.6, fontSize: 11, textTransform: 'capitalize' }}>
            · {userProfile?.title || userRole || 'user'}
          </span>
        </span>
      ),
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'theme',
      icon: <Palette size={14} />,
      label: 'Theme',
      onClick: () => setShowThemeModal(true),
    },
    {
      key: 'logout',
      icon: <LogOut size={14} />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  return (
    <>
      <div style={{
        padding: '15px',
        flexShrink: 0,
      }}>
        <Dropdown
          menu={{ items: menuItems }}
          placement="topRight"
          trigger={['click']}
          open={menuOpen}
          onOpenChange={setMenuOpen}
        >
          <div id="profile-chip-trigger" style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid var(--theme-border, #D9D2C4)',
            transition: '0.15s',
          }}>
            <div style={{
              width: 35,
              height: 35,
              borderRadius: '50%',
              background: 'var(--theme-blueLight, #E0EEF1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--theme-dataBlue, #005F73)',
              flexShrink: 0,
            }}>
              {displayName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{
                fontSize: 15,
                fontWeight: 600,
                color: 'var(--theme-text)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {displayName}
              </div>
              <div style={{ fontSize: 12, color: 'var(--theme-textTertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {userProfile?.title || userProfile?.member_role || userRole || 'user'}
              </div>
            </div>
          </div>
        </Dropdown>
      </div>

      <Modal
        title={<span><Paintbrush size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />Select Theme</span>}
        open={showThemeModal}
        onCancel={() => setShowThemeModal(false)}
        footer={null}
        width={500}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <h4 style={{ marginBottom: 12 }}>Mode</h4>
            <Radio.Group
              value={currentMode}
              buttonStyle="solid"
              size="large"
              onChange={(e) => {
                const newMode = e.target.value;
                const first = newMode === 'light' ? lightThemes[0][0] : darkThemes[0][0];
                changeTheme(first);
              }}
            >
              <Radio.Button value="light">Light</Radio.Button>
              <Radio.Button value="dark">Dark</Radio.Button>
            </Radio.Group>
          </div>
          <div>
            <h4 style={{ marginBottom: 12 }}>Color</h4>
            <Radio.Group
              value={currentTheme}
              onChange={(e) => changeTheme(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {(currentMode === 'light' ? lightThemes : darkThemes).map(([key, theme]) => (
                  <Card
                    key={key}
                    size="small"
                    hoverable
                    onClick={() => changeTheme(key)}
                    style={{
                      cursor: 'pointer',
                      border: currentTheme === key ? `2px solid ${theme.primary}` : '1px solid #d9d9d9',
                      background: currentTheme === key ? theme.background : 'transparent',
                    }}
                  >
                    <Radio value={key} style={{ width: '100%' }}>
                      <Space>
                        <span style={{ fontWeight: currentTheme === key ? 600 : 400 }}>{theme.name}</span>
                        <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                          <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.primary, border: '1px solid rgba(0,0,0,0.1)' }} />
                          <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.secondary, border: '1px solid rgba(0,0,0,0.1)' }} />
                          <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.logoGradient, border: '1px solid rgba(0,0,0,0.1)' }} />
                        </div>
                      </Space>
                    </Radio>
                  </Card>
                ))}
              </Space>
            </Radio.Group>
          </div>
        </Space>
      </Modal>
    </>
  );
}
